import React, { useState, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useStyles } from "./ChooseDeliveryFlowLink.style";
import ChooseDeliveryFlowLinkProperties from "./ChooseDeliveryFlowLinkProperties";
import DeliveryFlowList from "./DeliveryFlowList/DeliveryFlowList";
import AllowAccess from "../../Access/AllowAccess/AllowAccess";
import Access from "../../Access/AllowAccess/Access";
import { useUserContext } from "../../../contexts/UserContext";
import ButtonSize from "../../../ui_components/Button/ButtonSize";
import ButtonType from "../../../ui_components/Button/ButtonType";
import SidePanel from "../../../ui_components/SidePanel/SidePanel";
import ChooseDeliveryPersonLinks from "../../Buttons/ChooseDeliveryPersonLinks/ChooseDeliveryPersonLinks";
import ServiceName from "../../../entities/ServiceName";
import DeliveryPerson from "../../../entities/DeliveryPerson";
import DeliveryStepStatus from "../../../entities/DeliveryStepStatus";
import DeliveryStepResponse from "../../../model/articleDelivery/DeliveryStepResponse";
import WarehouseStepResponse from "../../../model/articleDelivery/WarehouseStepResponse";
import WarehouseStepStatus from "../../../entities/WarehouseStepStatus";
import ShowDeliveryStepStatus from "../ShowDeliveryStepStatus/ShowDeliveryStepStatus";
import Link from "../../../ui_components/Link/Link";
import Currency from "../../../entities/Currency";
import Button from "../../../ui_components/Button/Button";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import WarehouseIcon from "@mui/icons-material/Warehouse";

const ChooseDeliveryFlowLink: React.FC<ChooseDeliveryFlowLinkProperties> = ({
  articleDeliveryId,
  articleDeliveryStatus,
  deliveryFlow,
  warehouseStep = null,
  haveDeliveryPersonSelected,
  orderByGeosIdProximity,
  readOnly = false,
  isButton = false,
  onClick = () => {},
  onSelect = () => {},
  onClose = () => {},
  disabled = false,
  showText = true,
  showIcon = true,
  rounded = false,
  size = ButtonSize.DEFAULT,
  type = ButtonType.DEFAULT,
  className = "",
}) => {
  const classes = useStyles();
  const userStore = useUserContext();

  const [newDeliveryFlow, setNewDeliveryFlow] = useState<
    DeliveryStepResponse[]
  >([...deliveryFlow]);
  const [newWarehouseStep, setNewWarehouseStep] = useState<WarehouseStepResponse | null>(warehouseStep);
  const [openSidePanel, setOpenSidePanel] = useState(false);
  const [loading] = useState(false);

  const handleOpenSidePanel = useCallback(() => {
    setOpenSidePanel(true);
    setNewWarehouseStep(warehouseStep);
    onClick && onClick();
  }, [onClick, warehouseStep]);

  const handleCloseSidePanel = useCallback(
    (callOnClose: boolean) => {
      setNewDeliveryFlow([...deliveryFlow]);
      setNewWarehouseStep(warehouseStep);
      setOpenSidePanel(false);
      callOnClose && onClose();
    },
    [deliveryFlow, warehouseStep, onClose]
  );

  const handleUpdate = useCallback((updatedFlow: DeliveryStepResponse[]) => {
    setNewDeliveryFlow(updatedFlow);
  }, []);

  const handleWarehouseStepChange = useCallback((step: WarehouseStepResponse | null) => {
    setNewWarehouseStep(step);
  }, []);

  const handleSave = useCallback(
    (data: { deliveryFlow: DeliveryStepResponse[], resetFlow: boolean, warehouseStep?: WarehouseStepResponse | null }) => {
      onSelect(data);
      handleCloseSidePanel(false);
    },
    [onSelect, handleCloseSidePanel]
  );

  const handleWarehouseDispatched = useCallback(() => {
    handleCloseSidePanel(false);
    onClose();
  }, [handleCloseSidePanel, onClose]);

  const showDeliveryPersonName = userStore.hasAccess(
    Access.DISPLAY_DELIVERY_PERSON_IN_ORDERS
  );

  if (!userStore.isRootAgency() && !showDeliveryPersonName) {
    return <></>;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const onDeliveryPersonSelect = useCallback(
    (deliveryPerson?: DeliveryPerson) => {
      if (
        !deliveryPerson ||
        newDeliveryFlow.findIndex(
          (t) => t.deliveryPersonId === deliveryPerson.id
        ) !== -1
      )
        return;

      setNewDeliveryFlow((prev) => {
        const updated = prev.map((step) => ({
          ...step,
          payArticleConfectionPriceToDeliveryPerson: false,
          current: false,
        }));
        return [
          ...updated,
          {
            id: "",
            deliveryPersonId: deliveryPerson.id,
            deliveryPersonName: deliveryPerson.name,
            deliveryCost: 0,
            deliveryCostCurrency: Currency.DOLLAR,
            order: prev.length + 1,
            status: DeliveryStepStatus.PENDING,
            assignedAt: 0,
            current: true,
            payArticleConfectionPriceToDeliveryPerson: true,
          },
        ];
      });
    },
    [newDeliveryFlow]
  );

  const current = newDeliveryFlow.find((t) => t.current);

  const warehouseDispatched = warehouseStep?.status === WarehouseStepStatus.DISPATCHED;
  // Se não há repartidores no fluxo (entrega direta almacén -> cliente), o almacén
  // continua sendo o "step" relevante mesmo depois de despachado.
  const showWarehouseInSummary = !!warehouseStep && (!warehouseDispatched || deliveryFlow.length === 0);

  const Btn = () => {
    if (isButton) {
      return (
        <Button
          text={"Asignar"}
          onClick={handleOpenSidePanel}
          fullWidth
          disabled={disabled}
          type={ButtonType.DEFAULT}
          startIcon={<DeliveryDiningIcon />}
        />
      );
    } else {
      return (
        <>
          {showWarehouseInSummary ? (
            <>
              <div className={classes.title}>{`Almacén:`}</div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <WarehouseIcon style={{ fontSize: "16px", color: "#f59e0b" }} />
                <span>{warehouseStep?.warehouseName ?? warehouseStep?.warehouseId}</span>
              </div>
              {!warehouseDispatched && (
                <AllowAccess any={[Access.SET_MONEY_DELIVERY_DELIVERY_PERSON]}>
                  <Link
                    text={readOnly ? "Ver" : "Cambiar"}
                    onClick={handleOpenSidePanel}
                  ></Link>
                </AllowAccess>
              )}
              {warehouseDispatched && (
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#065f46",
                    backgroundColor: "#d1fae5",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    width: "fit-content",
                  }}
                >
                  Despachado
                </span>
              )}
            </>
          ) : (
            <>
              <div className={classes.title}>{`Repartidor:`}</div>
              <div>{current?.deliveryPersonName ?? "-"}</div>
              <AllowAccess any={[Access.SET_MONEY_DELIVERY_DELIVERY_PERSON]}>
                <Link
                  text={readOnly ? "Ver" : (haveDeliveryPersonSelected ? "Cambiar" : "Asignar")}
                  onClick={handleOpenSidePanel}
                ></Link>
              </AllowAccess>
              <ShowDeliveryStepStatus status={current?.status} />
            </>
          )}
        </>
      );
    }
  };

  return (
    <AllowAccess any={[Access.DISPLAY_DELIVERY_PERSON_IN_ORDERS]}>
      <Btn />
      <SidePanel
        open={openSidePanel}
        onClose={() => handleCloseSidePanel(true)}
        title="Gestionar Orden de Repartidores"
      >
        <Box className={classes.sidePanelContent}>
          <div className={classes.side_panel_header_container}>
            <Box className={classes.sidePanelHeader}>
              <Typography className={classes.sidePanelTitle}>
                Orden de Entrega
              </Typography>
              {loading && <CircularProgress size={24} />}
            </Box>
            <Box>
              {!readOnly && (
                <ChooseDeliveryPersonLinks
                  haveDeliveryPersonSelected={false}
                  orderByGeosIdProximity={orderByGeosIdProximity}
                  service={ServiceName.ARTICLE_DELIVERY}
                  onClick={() => {}}
                  onSelect={onDeliveryPersonSelect}
                ></ChooseDeliveryPersonLinks>
              )}
            </Box>
          </div>
          <DeliveryFlowList
            articleDeliveryId={articleDeliveryId}
            articleDeliveryStatus={articleDeliveryStatus}
            haveDeliveryPersonSelected={haveDeliveryPersonSelected}
            readOnly={readOnly}
            deliveryFlow={newDeliveryFlow}
            warehouseStep={newWarehouseStep}
            onUpdate={handleUpdate}
            onWarehouseStepChange={handleWarehouseStepChange}
            onSave={handleSave}
            onWarehouseDispatched={handleWarehouseDispatched}
            loading={loading}
          />
        </Box>
      </SidePanel>
    </AllowAccess>
  );
};

export default ChooseDeliveryFlowLink;
